/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  IonPage,
  IonContent,
  IonLoading,
  IonAlert,
} from "@ionic/react";
import axios from "axios";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useAuthInit } from "../Auth";
import {
  requestID,
  baseUrl,
  setUserSession,
  config,
  APP_VERSION,
  APP_COPYRIGHT,
  setBusinessData,
} from "../common/utils";
import {
  Box,
  Button,
  Container,
  InputAdornment,
  Paper,
  Typography,
  Grid,
  Divider,
  useMediaQuery,
  useTheme as useMuiTheme,
  Alert,
  CircularProgress,
  Avatar,
  styled,
  FormControl,
  InputLabel,
  OutlinedInput,
  Link as MuiLink,
  createTheme,
  FormHelperText,
  IconButton,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import {
  LockOutlined,
  ArrowBack,
  Visibility,
  VisibilityOff,
  LocationOn,
} from "@mui/icons-material";
import { ThemeProvider } from "@emotion/react";

// ============================================
// Modern Dashboard Theme (MUI5) - Matching DeltaGovPortal
// ============================================
const theme = createTheme({
  palette: {
    primary: {
      main: "#243b5a", // deep navy from spec
    },
    background: {
      default: "#f4f6f8",
      paper: "#ffffff",
    },
    text: {
      primary: "#1f2937",
      secondary: "#6b7280",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "2rem",
      fontWeight: 700,
      color: "#1f2937",
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    body1: {
      fontSize: "0.875rem",
      color: "#6b7280",
    },
    body2: {
      fontSize: "0.875rem",
      color: "#374151",
    },
    caption: {
      fontSize: "0.75rem",
      letterSpacing: "1px",
      color: "#6b7280",
      textTransform: "uppercase",
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 600,
      color: "#ffffff",
    },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "none",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          justifyContent: "space-between",
          padding: "12px 16px",
          borderRadius: 6,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#d1d5db",
          borderWidth: "1px",
        },
      },
    },
  },
});

// ============================================
// Styled Components - Matching DeltaGovPortal
// ============================================
const Header = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  height: "90px",
  padding: "0 40px",
  display: "flex",
  alignItems: "center",
  width: "100%",
  boxSizing: "border-box",
}));

const LogoCircle = styled(Box)({
  width: "55px",
  height: "55px",
  borderRadius: "50%",
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  color: "#243b5a",
  fontWeight: "bold",
  border: "2px solid #f0c45a",
  boxSizing: "border-box",
  marginRight: "16px",
});

const FooterLink = styled(MuiLink)({
  color: "#d1d5db",
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": {
    color: "#ffffff",
  },
});

const StyledCard = styled(Paper)({
  width: "450px",
  maxWidth: "100%",
  backgroundColor: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: "6px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  margin: "0 auto",
});

const LoginButton = styled(Button)({
  height: "45px",
  backgroundColor: "#243b5a",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "14px",
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  borderRadius: "6px",
  padding: "0 16px",
  "&:hover": {
    backgroundColor: "#162c47",
  },
});

const StyledFormControl = styled(FormControl)({
  "& .MuiOutlinedInput-root": {
    height: "42px",
    backgroundColor: "#f9fafb",
    "& fieldset": {
      borderColor: "#d1d5db",
      borderRadius: "6px",
    },
    "&:hover fieldset": {
      borderColor: "#d1d5db",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#243b5a",
      borderWidth: "1px",
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "13px",
    color: "#374151",
    fontWeight: 500,
    transform: "translate(12px, -8px) scale(0.85)",
    "&.Mui-focused": {
      color: "#374151",
    },
  },
  "& .MuiFormLabel-asterisk": {
    color: "#ef4444",
    marginLeft: "4px",
  },
});

// ============================================
// Main LoginCie Component - Using local state and onClick (like school login)
// ============================================
const LoginCie: React.FC = () => {
  const [isInternet, setIsInternet] = useState({ openAlert: false, message: "" });
  const [status, setStatus] = useState({ loading: false, error: false });
  const history = useHistory();
  const [userData, setUserData] = useState<any>(null);

  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  // Local state for form fields - initially empty
  const [localGovernment, setLocalGovernment] = useState("");
  const [accessCode, setAccessCode] = useState("");

  // Local state for field errors
  const [errors, setErrors] = useState({
    localGovernment: "",
    accessCode: "",
  });

  const authInitState = useAuthInit();



  // Password visibility
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const localGovernments = [
    { id: 1, name: "Aniocha North" },
    { id: 2, name: "Aniocha South" },
    { id: 3, name: "Bomadi" },
    { id: 4, name: "Burutu" },
    { id: 5, name: "Ethiope East" },
    { id: 6, name: "Ethiope West" },
    { id: 7, name: "Ika North East" },
    { id: 8, name: "Ika South" },
    { id: 9, name: "Isoko North" },
    { id: 10, name: "Isoko South" },
    { id: 11, name: "Ndokwa East" },
    { id: 12, name: "Ndokwa West" },
    { id: 13, name: "Okpe" },
    { id: 14, name: "Oshimili North" },
    { id: 15, name: "Oshimili South" },
    { id: 16, name: "Patani" },
    { id: 17, name: "Sapele" },
    { id: 18, name: "Udu" },
    { id: 19, name: "Ughelli North" },
    { id: 20, name: "Ughelli South" },
    { id: 21, name: "Ukwuani" },
    { id: 22, name: "Uvwie" },
    { id: 23, name: "Warri North" },
    { id: 24, name: "Warri South" },
    { id: 25, name: "Warri South West" },
  ];

  // Validate fields before login
  const validateFields = (): boolean => {
    const newErrors = {
      localGovernment: "",
      accessCode: "",
    };
    let isValid = true;

    if (!localGovernment) {
      newErrors.localGovernment = "Local Government is required";
      isValid = false;
    }
    if (!accessCode.trim()) {
      newErrors.accessCode = "Access code is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Login handler - called directly from button onClick (like school login)
  const handleLogin = async () => {
    // Validate fields first
    if (!validateFields()) {
      return;
    }

    setStatus({ loading: true, error: false });
    console.log(localGovernment, accessCode);
    // return;
    try {
      const response = await axios.post(
        baseUrl() + "cies/login", // Updated endpoint for inspectors
        {
          lgaCode: localGovernment, // Send LGA name as parameter
          inspectorCode: accessCode,    // Send access code as password
          requestID: requestID(),
        },
        config
      );
      console.log({ response });


      const userDatax = response.data.data;

      setUserSession(
        `${userDatax.inspectorId}*${userDatax.lgaCode}*${userDatax.name}`,
        userDatax
      );
      console.log(response.data.message);


    } catch (error: any) {
      setStatus({ loading: false, error: true });
      // For testing - remove in production
      history.push("/cie_dashboard");
    //  window.location.replace("/cie_dashboard");

      // Uncomment for production error handling
      setIsInternet({
        openAlert: true,
        message: error.response?.data?.message || "An error occurred during login.",
      });
    }
  };

  // Handle return to main portal
  const handleReturnToMainPortal = () => {
    history.push("/login");
    window.location.replace("/login");
  };

  // Handle select change for local government
  const handleLocalGovernmentChange = (e: SelectChangeEvent) => {
    setLocalGovernment(e.target.value);
    if (errors.localGovernment) {
      setErrors({ ...errors, localGovernment: "" });
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <IonPage>
        <IonContent>
          {/* Full page layout */}
          <Box sx={{
            bgcolor: "background.default",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}>
            {/* HEADER - Matching DeltaGovPortal */}
            <Header>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <LogoCircle>⚖️</LogoCircle>
                <Box>
                  <Typography variant="caption" sx={{ color: "#cbd5e1", letterSpacing: "0.5px", display: "block" }}>
                    DELTA STATE GOVERNMENT
                  </Typography>
                  <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 600, lineHeight: 1.2 }}>
                    Ministry of Primary Education
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                    Inspector Portal
                  </Typography>
                </Box>
              </Box>
            </Header>

            {/* MAIN CONTENT - Centered Login Card */}
            <Box sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
              py: { xs: 4, md: 6 },
            }}>
              <StyledCard>
                <Box sx={{ p: 3 }}>
                  {/* Title */}
                  <Typography variant="h4" sx={{ fontSize: "1.25rem", fontWeight: 600, color: "#1f2937", mb: 0.5 }}>
                    CIE Inspector Portal
                  </Typography>

                  {/* Description */}
                  <Typography variant="body2" sx={{ color: "#6b7280", lineHeight: 1.5, mb: 2.5 }}>
                    Select your Local Government and enter your access code to proceed.
                  </Typography>

                  {/* Divider */}
                  <Divider sx={{ mx: -3, mb: 2.5, borderWidth: 1 }} />

                  {/* Form - without onSubmit */}
                  <Box>
                    {/* Local Government Dropdown */}
                    <StyledFormControl fullWidth variant="outlined" sx={{ mb: 2.5 }}>
                      <InputLabel
                        htmlFor="localGovernment"
                        sx={{
                          fontSize: "13px",
                          color: "#374151",
                          fontWeight: 500,
                          '& .MuiFormLabel-asterisk': {
                            color: "#ef4444",
                            marginLeft: '4px',
                          }
                        }}
                      >
                        Local Government <span style={{ color: "#ef4444", marginLeft: '4px' }}>*</span>
                      </InputLabel>
                      <Select
                        id="localGovernment"
                        value={localGovernment}
                        onChange={handleLocalGovernmentChange}
                        error={!!errors.localGovernment}
                        displayEmpty
                        startAdornment={
                          <InputAdornment position="start">
                            <LocationOn sx={{ color: "#9ca3af", fontSize: 20 }} />
                          </InputAdornment>
                        }
                        sx={{
                          height: "42px",
                          backgroundColor: "#f9fafb",
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: "#d1d5db",
                            borderRadius: "6px",
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: "#d1d5db",
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: "#243b5a",
                            borderWidth: "1px",
                          },
                        }}
                      >
                        <MenuItem value="" disabled>Select Local Government</MenuItem>
                        {localGovernments.map((lg) => (
                          <MenuItem key={lg.id} value={lg.id}>{lg.name}</MenuItem>
                        ))}
                      </Select>
                      {errors.localGovernment && (
                        <FormHelperText error>{errors.localGovernment}</FormHelperText>
                      )}
                    </StyledFormControl>

                    {/* Access Code Field with Eye Icon */}
                    <StyledFormControl fullWidth variant="outlined" sx={{ mb: 2.5 }}>
                      <InputLabel
                        htmlFor="accessCode"
                        sx={{
                          fontSize: "13px",
                          color: "#374151",
                          fontWeight: 500,
                          '& .MuiFormLabel-asterisk': {
                            color: "#ef4444",
                            marginLeft: '4px',
                          }
                        }}
                      >
                        Access Code <span style={{ color: "#ef4444", marginLeft: '4px' }}>*</span>
                      </InputLabel>
                      <OutlinedInput
                        id="accessCode"
                        type={showPassword ? 'text' : 'password'}
                        value={accessCode}
                        onChange={(e) => {
                          setAccessCode(e.target.value);
                          // Clear error when user types
                          if (errors.accessCode) {
                            setErrors({ ...errors, accessCode: "" });
                          }
                        }}
                        placeholder="Enter access code"
                        error={!!errors.accessCode}
                        startAdornment={
                          <InputAdornment position="start">
                            <LockOutlined sx={{ color: "#9ca3af", fontSize: 20 }} />
                          </InputAdornment>
                        }
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              edge="end"
                              sx={{ color: "#9ca3af" }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        }
                        sx={{
                          height: "42px",
                          backgroundColor: "#f9fafb",
                          fontSize: "14px",
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: "#d1d5db",
                            borderRadius: "6px",
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: "#d1d5db",
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: "#243b5a",
                            borderWidth: "1px",
                          },
                        }}
                      />
                      {errors.accessCode && (
                        <FormHelperText error>{errors.accessCode}</FormHelperText>
                      )}
                    </StyledFormControl>

                    {/* Submit Button - using onClick instead of type="submit" */}
                    <LoginButton
                      onClick={handleLogin}
                      fullWidth
                      disabled={status.loading}
                    >
                      {status.loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "ACCESS INSPECTOR PORTAL"}
                    </LoginButton>

                    {/* Divider */}
                    <Divider sx={{ my: 2, borderWidth: 1 }} />

                    {/* Return Link */}
                    <Button
                      onClick={handleReturnToMainPortal}
                      startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                      sx={{
                        color: "#243b5a",
                        fontSize: "13px",
                        textTransform: "none",
                        p: 0,
                        '&:hover': {
                          textDecoration: "underline",
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      Return to main portal
                    </Button>
                  </Box>
                </Box>
              </StyledCard>
            </Box>

            {/* Disclaimer */}
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.75rem",
                color: "#9ca3af",
                textAlign: "center",
                maxWidth: "500px",
                mx: "auto",
                px: 2,
                mb: 2,
                display: "block",
              }}
            >
              This is a restricted access portal. Unauthorized access attempts are logged and may be subject to legal action.
            </Typography>

            {/* FOOTER - Matching DeltaGovPortal exactly */}
            <Box component="footer" sx={{ bgcolor: "primary.main", color: "#d1d5db", py: { xs: 5, md: 6 }, mt: "auto" }}>
              <Container maxWidth="lg">
                <Grid container spacing={4} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      MINISTRY OF PRIMARY EDUCATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Delta State Government
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Asaba, Delta State, Nigeria
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      CONTACT INFORMATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Email: info@dsgmope.ng
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Address: Ministry Secretariat, Asaba
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Telephone: +234 (0)901 234 5678
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      OFFICIAL RESOURCES
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <FooterLink href="#">www.dsgmope.ng</FooterLink>
                    </Typography>
                    <Typography variant="body2">
                      <FooterLink href="#">Delta State Government Portal</FooterLink>
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: 3 }} />

                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem", maxWidth: 900, mx: "auto", mb: 2 }}
                >
                  OFFICIAL NOTICE: This is an official portal of the Delta State Government, Ministry of Primary Education. Unauthorized access, misuse, or tampering with this system is prohibited and may be subject to legal action. All information provided is for official purposes only.
                </Typography>

                <Typography variant="body2" align="center" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem" }}>
                  © 2026 Delta State Government, Ministry of Primary Education. All rights reserved.
                </Typography>
              </Container>
            </Box>
          </Box>

          {/* Loading & Alert */}
          <IonLoading isOpen={status.loading} message="Authenticating..." />
          <IonAlert
            isOpen={isInternet.openAlert}
            header="Login Alert"
            message={isInternet.message}
            buttons={['OK']}
            onDidDismiss={() => setIsInternet({ openAlert: false, message: "" })}
          />
        </IonContent>
      </IonPage>
    </ThemeProvider>
  );
};

export default LoginCie;